import AppConfig from '../AppConfig';
import { expandMediaAssetTags, normalizeMediaTags } from '../utils/mediaTagUtils';
import { uploadFileWithProgress } from '../utils/uploadWithProgress';
import http from './http';

/**
 * Authoring media API — resolving slide image keys to signed URLs (CEE-927).
 *
 * A generated image lands in `content.json` as an S3 key (`zones.image.imageKey`), and the
 * bucket is private, so the editor cannot render it directly. `GET /media/assets?s3_paths=`
 * takes the keys a deck references and returns each row with a `preview_url` presigned for
 * `PRESIGNED_URL_EXPIRY_SECONDS` (900s today).
 *
 * Batched, not one request per slide: the filter is a comma-separated set. Two independent caps
 * bound a batch — `MAX_S3_PATHS` (100) in the Lambda, and API Gateway's query-string limit, which
 * is the one that actually binds. See {@link MAX_QUERY_STRING_BYTES}.
 */

/** Max `s3_paths` entries the API accepts in one call (MAX_S3_PATHS in the media Lambda). */
export const MAX_RESOLVE_KEYS = 100;

/**
 * Bytes of query string API Gateway will accept on a request — measured against the deployed
 * preprod API, not taken from a doc page:
 *
 *     2048 bytes -> 204 with CORS headers   (reaches the Lambda)
 *     2049 bytes -> 403 ForbiddenException  (API Gateway answers; NO CORS headers)
 *
 * The 403 is why this cap has to be respected here rather than left to fail loudly. API Gateway
 * rejects the request itself, so its response carries no `Access-Control-Allow-Origin`, and a
 * browser will not expose a response without one — `fetch` rejects with `net::ERR_FAILED` and
 * reports "No 'Access-Control-Allow-Origin' header is present". The real 403 never reaches JS.
 * So overshooting this by one byte does not surface as a request error at the call site; it
 * surfaces as an unexplained CORS failure and every image on the deck stays blank.
 *
 * A resolvable key is ~119 bytes once `/` becomes `%2F`, plus 3 for the `%2C` separator, so the
 * ceiling is about 16 keys — nowhere near `MAX_RESOLVE_KEYS`. A 46-image deck sent as one request
 * built a 5,705-byte query string and 403'd, which is the bug this bound exists to prevent.
 */
export const MAX_QUERY_STRING_BYTES = 2048;

/**
 * What a batch is actually allowed to build. Short of the hard limit on purpose: the cost of a
 * spare request is one round trip, and the cost of exceeding the limit is a whole deck of blank
 * images reported as a CORS error.
 */
const QUERY_STRING_BUDGET = 1800;

/** Max collections per page (MAX_LIMIT in the media Lambda). */
export const MAX_COLLECTIONS_PAGE_SIZE = 100;

/** Max assets per page (MAX_LIMIT in the media Lambda). */
export const MAX_MEDIA_ASSETS_PAGE_SIZE = 100;

/**
 * Prefix every media asset lives under. `assertAllowedS3Path` in the media Lambda rejects
 * anything else, so keys from elsewhere — a PPT parser's extracted images, which land under
 * the template's own prefix — are filtered out here rather than sent and 400'd.
 */
const MEDIA_PREFIX = 'content-authoring/media-collections/';

export type MediaImageUse = 'content' | 'background';

/** `authoring_media_assets.media_kind` (CEE-1205). */
export type MediaKind = 'image' | 'video';

export interface MediaCollection {
  id: string;
  name: string;
  description: string | null;
  parent_collection_id: string | null;
  child_count: number;
  asset_count: number;
}

export interface MediaCollectionsPagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
}

export interface MediaUploadRequiredHeaders {
  'Content-Type': string;
  'x-amz-server-side-encryption': string;
}

export interface MediaUploadUrlData {
  upload_id: string;
  asset_id: string;
  upload_url: string;
  expires_at: string;
  s3_path: string;
  required_headers: MediaUploadRequiredHeaders;
}

export interface MediaUploadUrlRequest {
  filename: string;
  mime_type: string;
  file_size_bytes?: number;
  collection_id?: string | null;
  topic_id?: string | null;
  image_use?: MediaImageUse;
}

export interface RegisterMediaAssetRequest {
  upload_id: string;
  s3_path: string;
  filename: string;
  mime_type: string;
  collection_id?: string | null;
  tags?: string[];
  topic_id?: string | null;
  industry?: string | null;
  image_use?: MediaImageUse;
}

export interface RegisteredMediaAsset {
  id: string;
  s3_path: string;
  filename: string;
  preview_url?: string;
  preview_url_expires_at?: string;
  /**
   * Tags the worker derived for a generated image (CEE-778), or the matched asset's own.
   * Carried so Save to collection can prefill them — that PUT *replaces* the stored array,
   * so a save form starting empty would silently drop them.
   */
  tags?: string[];
}

export interface MediaAssetListItem {
  id: string;
  collection_id: string | null;
  filename: string;
  s3_path: string;
  mime_type: string | null;
  tags: string[] | null;
  file_size_bytes?: number | null;
  image_use?: MediaImageUse | string;
  ai_generated?: boolean;
  created_at?: string;
  updated_at?: string;
  preview_url?: string;
  preview_url_expires_at?: string;
  download_url?: string;
  download_url_expires_at?: string;
}

export interface ListMediaAssetsParams {
  page?: number;
  limit?: number;
  include_preview_urls?: boolean;
  include_download_urls?: boolean;
  image_use?: MediaImageUse;
  /** Scope assets to a collection folder. */
  collection_id?: string;
  /** Filename substring search (ILIKE). */
  q?: string;
  /** Tag overlap filter — assets must share at least one tag. */
  tags?: string[];
  /** Comma-separated asset UUIDs — returns matching assets and ignores pagination. */
  ids?: string[];
  /** `image` (the server default) or `video` (CEE-1205). Prefer {@link listVideoAssets}. */
  media_kind?: MediaKind;
}

interface MediaAssetsListResponseDto {
  assets: MediaAssetListItem[];
  pagination: MediaCollectionsPagination;
}

interface AssetDto {
  id: string;
  s3_path: string;
  preview_url?: string;
  preview_url_expires_at?: string;
}

interface AssetsResponseDto {
  assets: AssetDto[];
}

interface CollectionsResponseDto {
  collections: MediaCollection[];
  pagination: MediaCollectionsPagination;
}

interface MediaUploadUrlResponseDto {
  upload_id: string;
  asset_id: string;
  upload_url: string;
  expires_at: string;
  s3_path: string;
  required_headers: MediaUploadRequiredHeaders;
}

interface RegisterAssetResponseDto {
  asset: RegisteredMediaAsset & {
    collection_id: string | null;
    mime_type: string | null;
    tags: string[] | null;
  };
}

/** A resolvable key is a media-collection key: anything else the API would reject. */
export function isResolvableImageKey(imageKey: string): boolean {
  return imageKey.startsWith(MEDIA_PREFIX) && !imageKey.includes('..');
}

/** URL for the authoring API, tolerating a same-origin proxy base path. */
export function toAuthoringApiUrl(path: string): URL {
  const base = AppConfig.complianceAuthoringApiUrl.replace(/\/$/, '');
  const href = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  return href.startsWith('http') ? new URL(href) : new URL(href, window.location.origin);
}

async function readApiErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (body?.error?.message) {
      return body.error.message;
    }
    if (body?.message) {
      return body.message;
    }
  } catch {
    /* keep fallback */
  }
  return fallback;
}

export interface ListMediaCollectionsParams {
  page?: number;
  limit?: number;
  q?: string;
  /** UUID for a parent folder, or `root` for top-level collections. */
  parent_collection_id?: string;
}

/**
 * GET /media/collections — paginated media collection folders.
 */
export async function listMediaCollections(
  params: ListMediaCollectionsParams = {},
): Promise<{ collections: MediaCollection[]; pagination: MediaCollectionsPagination }> {
  const url = toAuthoringApiUrl('/media/collections');
  if (params.page !== undefined) {
    url.searchParams.set('page', String(params.page));
  }
  if (params.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit));
  }
  if (params.q) {
    url.searchParams.set('q', params.q);
  }
  if (params.parent_collection_id) {
    url.searchParams.set('parent_collection_id', params.parent_collection_id);
  }

  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to list media collections (${response.status})`));
  }

  const body = (await response.json()) as CollectionsResponseDto;
  return {
    collections: body.collections ?? [],
    pagination: body.pagination,
  };
}

/** Fetches every collection across pages (for location picker options). */
export async function listAllMediaCollections(): Promise<MediaCollection[]> {
  const collections: MediaCollection[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const result = await listMediaCollections({ page, limit: MAX_COLLECTIONS_PAGE_SIZE });
    collections.push(...result.collections);
    totalPages = result.pagination.total_pages;
    page += 1;
  }

  return collections;
}

/** Fetches every asset in a collection across pages (for callers that need the full list). */
export async function listAllMediaAssetsInCollection(
  params: Omit<ListMediaAssetsParams, 'page' | 'limit'> & { collection_id: string },
): Promise<MediaAssetListItem[]> {
  const assets: MediaAssetListItem[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const result = await listMediaAssets({
      ...params,
      page,
      limit: MAX_MEDIA_ASSETS_PAGE_SIZE,
    });
    assets.push(...result.assets);
    totalPages = result.pagination.total_pages;
    page += 1;
  }

  return assets;
}

/** Fetches child collections for a parent across pages (for callers that need the full list). */
export async function listAllMediaCollectionsForParent(
  params: Omit<ListMediaCollectionsParams, 'page' | 'limit'>,
): Promise<MediaCollection[]> {
  const collections: MediaCollection[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const result = await listMediaCollections({
      ...params,
      page,
      limit: MAX_COLLECTIONS_PAGE_SIZE,
    });
    collections.push(...result.collections);
    totalPages = result.pagination.total_pages;
    page += 1;
  }

  return collections;
}

/**
 * GET /media/assets — paginated media library assets.
 */
export async function listMediaAssets(
  params: ListMediaAssetsParams = {},
): Promise<{ assets: MediaAssetListItem[]; pagination: MediaCollectionsPagination }> {
  const url = toAuthoringApiUrl('/media/assets');
  if (params.page !== undefined) {
    url.searchParams.set('page', String(params.page));
  }
  if (params.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit));
  }
  if (params.image_use) {
    url.searchParams.set('image_use', params.image_use);
  }
  if (params.collection_id) {
    url.searchParams.set('collection_id', params.collection_id);
  }
  if (params.q) {
    url.searchParams.set('q', params.q);
  }
  if (params.tags?.length) {
    url.searchParams.set('tags', params.tags.join(','));
  }
  if (params.ids?.length) {
    url.searchParams.set('ids', params.ids.join(','));
  }
  if (params.include_preview_urls === false) {
    url.searchParams.set('include_preview_urls', 'false');
  }
  if (params.include_download_urls === true) {
    url.searchParams.set('include_download_urls', 'true');
  }
  if (params.media_kind) {
    url.searchParams.set('media_kind', params.media_kind);
  }

  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to list media assets (${response.status})`));
  }

  const body = (await response.json()) as MediaAssetsListResponseDto;
  return {
    assets: body.assets ?? [],
    pagination: body.pagination,
  };
}

/** GET /media/assets?ids= — fetch a single asset with preview URL. */
export async function getMediaAssetById(id: string): Promise<MediaAssetListItem | null> {
  const result = await listMediaAssets({
    ids: [id],
    include_preview_urls: true,
  });
  return result.assets[0] ?? null;
}

/** GET /media/assets?ids= — fetch presigned preview and optional attachment download URLs. */
export async function getMediaAssetDownloadUrl(id: string): Promise<MediaAssetListItem | null> {
  const result = await listMediaAssets({
    ids: [id],
    include_preview_urls: true,
    include_download_urls: true,
  });
  return result.assets[0] ?? null;
}

export interface UpdateMediaAssetRequest {
  tags?: string[];
  collection_id?: string | null;
  filename?: string;
}

interface UpdateAssetResponseDto {
  asset: MediaAssetListItem;
}

/**
 * Normalize a filename for save — if the author omits the extension, append the original; if
 * they supply one, keep it (CEE-1086, T-46).
 */
export function normalizeMediaAssetFilenameForSave(
  originalFilename: string,
  newName: string,
): string {
  const originalExt = originalFilename.includes('.')
    ? originalFilename.substring(originalFilename.lastIndexOf('.'))
    : '';

  if (!newName.includes('.') && originalExt) {
    return newName + originalExt;
  }

  return newName;
}

/** PUT /media/assets/{id} — update editable media asset properties. */
export async function updateMediaAsset(
  id: string,
  payload: UpdateMediaAssetRequest,
): Promise<MediaAssetListItem> {
  const body: Record<string, unknown> = {};
  if (payload.tags !== undefined) {
    body.tags = normalizeMediaTags(payload.tags);
  }
  if (payload.collection_id !== undefined) {
    body.collection_id = payload.collection_id;
  }
  if (payload.filename !== undefined) {
    body.filename = payload.filename.trim();
  }

  const response = await http.put(
    toAuthoringApiUrl(`/media/assets/${id}`).toString(),
    JSON.stringify(body),
  );

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to update media asset (${response.status})`));
  }

  const responseBody = (await response.json()) as UpdateAssetResponseDto;
  return responseBody.asset;
}

interface DeleteAssetResponseDto {
  id: string;
  status: string;
  deleted_by: string;
  deleted_at: string;
}

/** DELETE /media/assets/{id} — soft-delete a media asset (sets deleted_at). */
/**
 * DELETE /media/assets/{id}. An asset still used on slides is refused with
 * {@link MediaAssetInUseError} unless `confirm` is set — pass it only after the author has seen
 * the in-use warning.
 */
export async function deleteMediaAsset(
  id: string,
  options: { confirm?: boolean } = {},
): Promise<DeleteAssetResponseDto> {
  const url = toAuthoringApiUrl(`/media/assets/${id}`);
  if (options.confirm) {
    url.searchParams.set('confirm', 'true');
  }
  const response = await http.delete(url.toString());

  if (response.status === 409) {
    const body = await response.json().catch(() => null);
    if (body?.error?.code === 'asset_in_use') {
      throw new MediaAssetInUseError(id, Number(body.error.details?.usage_count ?? 0));
    }
    throw new Error(body?.error?.message ?? `Failed to delete media asset (${response.status})`);
  }

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to delete media asset (${response.status})`));
  }

  return (await response.json()) as DeleteAssetResponseDto;
}

export interface CreateMediaCollectionRequest {
  name: string;
  description?: string | null;
  parent_collection_id?: string | null;
}

interface CreateCollectionResponseDto {
  collection: MediaCollection;
}

/** POST /media/collections — create a new media collection folder (CEE-794). */
export async function createMediaCollection(
  payload: CreateMediaCollectionRequest,
): Promise<MediaCollection> {
  const response = await http.post(
    toAuthoringApiUrl('/media/collections').toString(),
    JSON.stringify(payload),
  );

  if (!response.ok) {
    throw new Error(
      await readApiErrorMessage(response, `Failed to create media collection (${response.status})`),
    );
  }

  const body = (await response.json()) as CreateCollectionResponseDto;
  return body.collection;
}

export interface UpdateMediaCollectionRequest {
  name?: string;
  description?: string | null;
  parent_collection_id?: string | null;
}

interface UpdateCollectionResponseDto {
  collection: MediaCollection;
}

/** PUT /media/collections/{id} — update a media collection folder. */
export async function updateMediaCollection(
  id: string,
  payload: UpdateMediaCollectionRequest,
): Promise<MediaCollection> {
  const response = await http.put(
    toAuthoringApiUrl(`/media/collections/${id}`).toString(),
    JSON.stringify(payload),
  );

  if (!response.ok) {
    throw new Error(
      await readApiErrorMessage(response, `Failed to update media collection (${response.status})`),
    );
  }

  const body = (await response.json()) as UpdateCollectionResponseDto;
  return body.collection;
}

interface DeleteCollectionResponseDto {
  id: string;
  status: string;
  deleted_by: string;
  deleted_at: string;
}

/** DELETE /media/collections/{id} — soft-delete an empty media collection folder. */
export async function deleteMediaCollection(id: string): Promise<DeleteCollectionResponseDto> {
  const response = await http.delete(
    toAuthoringApiUrl(`/media/collections/${id}`).toString(),
  );

  if (!response.ok) {
    throw new Error(
      await readApiErrorMessage(response, `Failed to delete media collection (${response.status})`),
    );
  }

  return (await response.json()) as DeleteCollectionResponseDto;
}

/** Collects every distinct tag already used on media assets (for autocomplete). */
async function listAllMediaTagsFromAssets(): Promise<string[]> {
  const seen = new Set<string>();
  const unique: string[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const result = await listMediaAssets({
      page,
      limit: MAX_MEDIA_ASSETS_PAGE_SIZE,
      include_preview_urls: false,
    });
    for (const asset of result.assets) {
      for (const tag of expandMediaAssetTags(asset.tags ?? [])) {
        if (seen.has(tag)) continue;
        seen.add(tag);
        unique.push(tag);
      }
    }
    totalPages = result.pagination.total_pages;
    page += 1;
  }

  return unique.sort((left, right) => left.localeCompare(right));
}

function shouldFallbackToAssetTagPagination(status: number): boolean {
  // Authoring API may lag the portal deploy; fall back to the pre-CEE-1129 path.
  // Gateway errors only — a 500 on /media/tags is a real bug; don't amplify with asset pagination.
  return status === 404 || status === 405 || status === 502 || status === 503 || status === 504;
}

/** Collects every distinct tag already used on media assets (for autocomplete). */
export async function listAllMediaTags(): Promise<string[]> {
  const response = await http.get(
    toAuthoringApiUrl('/media/tags').toString(),
  );

  if (!response.ok) {
    if (shouldFallbackToAssetTagPagination(response.status)) {
      return listAllMediaTagsFromAssets();
    }
    throw new Error(
      await readApiErrorMessage(response, `Failed to load media tags (${response.status})`),
    );
  }

  const body = (await response.json()) as { tags?: string[] };
  return expandMediaAssetTags(body.tags ?? []).sort((left, right) =>
    left.localeCompare(right),
  );
}

/**
 * The entity an editor-driven image generation belongs to (CEE-778, CEE-1072).
 *
 * Threaded from the editor page down to the generate panel so the worker can inherit the
 * template's title, type, topic and industry. Camel-cased because it crosses React props;
 * `generateMediaImage` maps it onto the snake_case request fields.
 */
export interface ImageGenerationEntityContext {
  entityType: 'template' | 'course';
  entityId: string;
}

/**
 * Which slide slot a Change-image generation is filling (CEE-1127).
 *
 * Carried as one object rather than three props so it stays absent-or-complete: a `layout` without
 * an `imagePath` tells the API nothing, and every consumer would have to re-check the pair.
 */
export interface ImageSlotContext {
  layout: string;
  variant?: string;
  /** Document path of the `imageKey` field, e.g. `interaction.items[0].imageKey`. */
  imagePath: string;
}

export interface GenerateMediaImageRequest {
  prompt: string;
  collection_id?: string | null;
  force_generate?: boolean;
  tags?: string[];
  /**
   * An explicit shape, for a caller that has one — a media-library generation with no slide, say.
   * No caller sets either today, and neither is defaulted: a slot (below) beats both server-side,
   * so guessing here would only be wrong for the slots that are not 16:9 content images.
   */
  image_use?: MediaImageUse;
  aspect_ratio?: string;
  /**
   * The template or course the image is being generated for (CEE-778, CEE-1072).
   *
   * The worker reads the row behind these two to inherit the template's title, type, topic and
   * industry — the title and type describe the image for tag derivation, the industry dresses
   * the people in it. Deliberately NOT `topic_id` / `industry` sent from here: both are match
   * keys in `findMatchingMediaAsset`, which admits a row on industry alone when no tags are
   * given and needs one point to clear its minimum score. Sending them would turn the first
   * Generate click (`force_generate: false`) into "hand me any image from this industry".
   */
  entity_type?: 'template' | 'course';
  entity_id?: string;
  /**
   * The slot the image is going into (CEE-1127) — the slide's layout id, its variant, and the
   * document path of the `imageKey` being filled (`interaction.items[0].imageKey`).
   *
   * Sent instead of an `aspect_ratio` and an `image_use` guessed here. The API matches the path
   * against the interaction registry and derives both, because the registry lives in the Lambda
   * shared package and cannot be imported into this bundle — mirroring its ~16 slots and their
   * ratios by hand here is exactly the drift the registry exists to prevent. Omit all three for a
   * media-library generation, which has no slide.
   */
  layout?: string;
  variant?: string;
  image_path?: string;
}

export interface MediaGenerationAsset {
  id: string;
  s3_path: string;
  filename: string;
  preview_url?: string;
  preview_url_expires_at?: string;
  mime_type?: string | null;
  tags?: string[];
}

export interface GenerateMediaImageResult {
  matched: boolean;
  asset?: MediaGenerationAsset;
  job?: { id: string; status: string };
}

interface GenerateMediaImageResponseDto {
  matched: boolean;
  asset?: MediaGenerationAsset;
  job?: { id: string; status: string };
}

/**
 * API codes whose message is written for the author, not a developer: it says what to change in
 * the prompt (the personal data to remove, the kind of request generated images refuse — CEE-1333).
 */
const AUTHOR_FACING_GENERATE_ERROR_CODES = new Set([
  'prompt_contains_sensitive_data',
  'prompt_not_allowed',
]);

/** A refused generate-image request whose message is safe to show the author as-is. */
export class GenerateMediaImagePromptError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'GenerateMediaImagePromptError';
  }
}

/** POST /media/generate-image — match an existing asset or queue async generation. */
export async function generateMediaImage(
  request: GenerateMediaImageRequest,
  options?: { signal?: AbortSignal },
): Promise<GenerateMediaImageResult> {
  const response = await http.post(
    toAuthoringApiUrl('/media/generate-image').toString(),
    JSON.stringify({
      prompt: request.prompt.trim(),
      ...(request.collection_id != null ? { collection_id: request.collection_id } : {}),
      // Neither `image_use` nor `aspect_ratio` is defaulted here any more (CEE-1127). They used to
      // be pinned to `content` / `16:9`, which is the same browser-side guess this change removed
      // from the Change-image panel — and only 3 of the registry's ~16 slots are 16:9. The API
      // derives both from `layout` + `image_path` below, and applies its own identical defaults
      // (`DEFAULT_MEDIA_ASPECT_RATIO`, `parseImageUse`) when there is no slot. So a value goes on
      // the wire only when a caller deliberately asked for one.
      ...(request.image_use ? { image_use: request.image_use } : {}),
      ...(request.aspect_ratio ? { aspect_ratio: request.aspect_ratio } : {}),
      force_generate: request.force_generate ?? true,
      tags: request.tags ?? [],
      // Omitted rather than sent null when there is no context — the API treats both fields as
      // optional strings and a standalone media-library generation has no owning entity.
      ...(request.entity_type && request.entity_id
        ? { entity_type: request.entity_type, entity_id: request.entity_id }
        : {}),
      // Same omission rule. `layout` + `image_path` are what the API needs to find the slot;
      // `variant` only narrows it, so it rides along when the slide has one.
      ...(request.layout && request.image_path
        ? {
            layout: request.layout,
            image_path: request.image_path,
            ...(request.variant ? { variant: request.variant } : {}),
          }
        : {}),
    }),
    { signal: options?.signal },
  );

  if (!response.ok) {
    const fallback = `Failed to generate media image (${response.status})`;
    let body: { error?: { code?: string; message?: string }; message?: string } | undefined;
    try {
      body = await response.json();
    } catch {
      /* keep fallback */
    }
    const error = body?.error;
    if (error?.code && error.message && AUTHOR_FACING_GENERATE_ERROR_CODES.has(error.code)) {
      throw new GenerateMediaImagePromptError(error.code, error.message);
    }
    // Same precedence as `readApiErrorMessage`: the API's `{ error: { message } }`, then a
    // gateway/proxy's top-level `{ message }`, then the status line.
    throw new Error(error?.message || body?.message || fallback);
  }

  const body = (await response.json()) as GenerateMediaImageResponseDto;
  return {
    matched: body.matched,
    asset: body.asset,
    job: body.job,
  };
}

export type MediaJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/**
 * Cancels a queued or running async authoring job (`POST /jobs/{id}/cancel`, CEE-1263).
 * Cancelable types today: `image_generation`, `scorm_export`.
 */
export async function cancelAuthoringJob(jobId: string): Promise<void> {
  const response = await http.post(
    toAuthoringApiUrl(`/jobs/${jobId}/cancel`).toString(),
    JSON.stringify({}),
  );
  if (!response.ok) {
    throw new Error(
      await readApiErrorMessage(response, `Failed to cancel job (${response.status})`),
    );
  }
}

export interface MediaGenerationJob {
  id: string;
  status: MediaJobStatus;
  progress_message?: string | null;
  error_message?: string | null;
  output_summary?: {
    asset_id?: string;
    s3_path?: string;
    filename?: string;
    preview_url?: string;
    preview_url_expires_at?: string;
    mime_type?: string;
    /** Tags the worker derived for the generated asset (CEE-778). */
    tags?: string[];
  } | null;
}

interface GetMediaJobResponseDto {
  job: MediaGenerationJob;
}

/** GET /jobs/{id} — poll async image generation job status. */
export async function getMediaGenerationJob(jobId: string): Promise<MediaGenerationJob> {
  const response = await http.get(toAuthoringApiUrl(`/jobs/${jobId}`).toString());

  if (!response.ok) {
    throw new Error(
      await readApiErrorMessage(response, `Failed to load media generation job (${response.status})`),
    );
  }

  const body = (await response.json()) as GetMediaJobResponseDto;
  return body.job;
}

export function toRegisteredMediaAsset(asset: MediaGenerationAsset): RegisteredMediaAsset {
  const filename = asset.filename || asset.s3_path.split('/').pop() || 'generated-image.jpg';
  return {
    id: asset.id,
    s3_path: asset.s3_path,
    filename,
    preview_url: asset.preview_url,
    preview_url_expires_at: asset.preview_url_expires_at,
    tags: asset.tags,
  };
}

export function mediaGenerationJobToRegisteredAsset(job: MediaGenerationJob): RegisteredMediaAsset | null {
  const summary = job.output_summary;
  if (!summary?.asset_id || !summary.s3_path) return null;
  return toRegisteredMediaAsset({
    id: summary.asset_id,
    s3_path: summary.s3_path,
    filename:
      summary.filename ??
      summary.s3_path.split('/').pop() ??
      'generated-image.jpg',
    preview_url: summary.preview_url,
    preview_url_expires_at: summary.preview_url_expires_at,
    mime_type: summary.mime_type ?? null,
    tags: summary.tags,
  });
}

const DEFAULT_JOB_POLL_INTERVAL_MS = 2000;

function waitWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Aborted'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('Aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Polls GET /jobs/{id} until the job succeeds or fails. */
export async function pollMediaGenerationJob(
  jobId: string,
  options?: {
    intervalMs?: number;
    signal?: AbortSignal;
    onProgress?: (job: MediaGenerationJob) => void;
  },
): Promise<MediaGenerationJob> {
  const intervalMs = options?.intervalMs ?? DEFAULT_JOB_POLL_INTERVAL_MS;

  while (true) {
    if (options?.signal?.aborted) {
      throw new Error('Aborted');
    }

    const job = await getMediaGenerationJob(jobId);
    options?.onProgress?.(job);
    if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
      return job;
    }
    await waitWithAbort(intervalMs, options?.signal);
  }
}

export const MEDIA_COLLECTION_ROOT_LABEL = 'Media collection';

/** Builds a display path such as `Media collection / Anti-Bribery`. */
export function formatMediaCollectionPath(
  collection: MediaCollection,
  collectionsById: ReadonlyMap<string, MediaCollection>,
): string {
  const names: string[] = [];
  let current: MediaCollection | undefined = collection;
  while (current) {
    names.unshift(current.name);
    current = current.parent_collection_id
      ? collectionsById.get(current.parent_collection_id)
      : undefined;
  }
  return `${MEDIA_COLLECTION_ROOT_LABEL} / ${names.join(' / ')}`;
}

export function buildMediaCollectionOptions(collections: readonly MediaCollection[]) {
  const byId = new Map(collections.map((collection) => [collection.id, collection]));
  return collections
    .map((collection) => ({
      value: collection.id,
      label: formatMediaCollectionPath(collection, byId),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

/** Parses comma-separated tags from the upload form into the API's string array. */
export function parseMediaTagsInput(value: string): string[] {
  return value
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter((tag) => tag.length > 0);
}

function mimeTypeFromFilename(filename: string): string {
  const extension = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  switch (extension) {
    case '.png':
      return 'image/png';
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.gif':
      return 'image/gif';
    case '.webp':
      return 'image/webp';
    default:
      return 'application/octet-stream';
  }
}

/**
 * POST /media/upload-url — presigned S3 PUT URL for an image upload (CEE-919).
 */
export async function requestMediaUploadUrl(
  payload: MediaUploadUrlRequest,
): Promise<MediaUploadUrlData> {
  const response = await http.post(
    toAuthoringApiUrl('/media/upload-url').toString(),
    JSON.stringify({
      filename: payload.filename,
      mime_type: payload.mime_type,
      file_size_bytes: payload.file_size_bytes,
      collection_id: payload.collection_id ?? null,
      topic_id: payload.topic_id ?? null,
      image_use: payload.image_use ?? 'background',
    }),
  );

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to get upload URL (${response.status})`));
  }

  return (await response.json()) as MediaUploadUrlResponseDto;
}

/** PUT file bytes to S3 using the presigned URL from {@link requestMediaUploadUrl}. */
export async function uploadMediaToS3(
  upload: MediaUploadUrlData,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await uploadFileWithProgress(
    upload.upload_url,
    file,
    onProgress ?? (() => undefined),
    { headers: { ...upload.required_headers } },
  );
}

/**
 * POST /media/assets — register an uploaded image after the S3 PUT succeeds (CEE-919).
 */
export async function registerMediaAsset(
  payload: RegisterMediaAssetRequest,
): Promise<RegisteredMediaAsset> {
  const response = await http.post(
    toAuthoringApiUrl('/media/assets').toString(),
    JSON.stringify({
      upload_id: payload.upload_id,
      s3_path: payload.s3_path,
      filename: payload.filename,
      mime_type: payload.mime_type,
      collection_id: payload.collection_id ?? null,
      tags: payload.tags ?? [],
      topic_id: payload.topic_id ?? null,
      industry: payload.industry ?? null,
      image_use: payload.image_use ?? 'background',
    }),
  );

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to register media asset (${response.status})`));
  }

  const body = (await response.json()) as RegisterAssetResponseDto;
  return body.asset;
}

/** Full upload flow: presign → S3 PUT → register. */
export async function uploadAndRegisterMediaAsset(params: {
  file: File;
  collectionId: string | null;
  tags: string[];
  topicId?: string | null;
  imageUse?: MediaImageUse;
}): Promise<RegisteredMediaAsset> {
  const mimeType = params.file.type || mimeTypeFromFilename(params.file.name);
  const upload = await requestMediaUploadUrl({
    filename: params.file.name,
    mime_type: mimeType,
    file_size_bytes: params.file.size,
    collection_id: params.collectionId,
    topic_id: params.topicId ?? null,
    image_use: params.imageUse ?? 'background',
  });

  await uploadMediaToS3(upload, params.file);

  return registerMediaAsset({
    upload_id: upload.upload_id,
    s3_path: upload.s3_path,
    filename: params.file.name,
    mime_type: mimeType,
    collection_id: params.collectionId,
    tags: params.tags,
    topic_id: params.topicId ?? null,
    industry: null,
    image_use: params.imageUse ?? 'background',
  });
}

// ─── Video (CEE-1205, CEE-1348) ─────────────────────────────────────────────────────────────────
//
// presign (POST /media/upload-url, media_kind "video") → S3 presigned POST via
// uploadFileWithProgress → register (POST /media/assets) → poll GET /media/assets/{id}
// (useVideoAssetStatus) until the scan and probe reach a terminal state. TAR §2.2–2.5.
//
// Size and type limits are NOT checked here: they come from ui-config (T13) and the caller
// validates before presigning. The server repeats both checks.

/** Malware scan state as reported by the media API (`shared/src/media/videoScanPolicy.ts`). */
export type VideoScanState = 'clean' | 'scanning' | 'blocked' | 'infected';

/** `authoring_media_assets.video_probe_state`. */
export type VideoProbeState = 'pending' | 'ok' | 'rejected';

/** Reasons `GET /media/assets/{id}` can report for a rejected probe (TAR §2.6). */
export type VideoProbeReason =
  | 'probe_failed'
  | 'container_mismatch'
  | 'codec_unsupported'
  | 'resolution_exceeded'
  | 'duration_unknown';

/**
 * PROVISIONAL — pending T3 (video presign, held for the ST-01 spike). Coded to the TAR §2.2
 * contract: an S3 presigned POST whose policy carries `content-length-range` and
 * `eq $Content-Type`. `fields` is posted verbatim, before the file; it must therefore include
 * `key`, `Content-Type` and the signature fields. Re-check against `createUploadUrl.ts` once T3
 * lands.
 */
export interface VideoUploadPost {
  /** Form action URL (the bucket endpoint). */
  url: string;
  /** Policy fields, posted in order before the file. */
  fields: Record<string, string>;
}

/**
 * PROVISIONAL — pending T3. TAR §2.2 response: `{ upload_id, s3_path, post: { url, fields } }`.
 * `collection_id` and `expires_at` are not in the TAR's response, but register requires
 * `collection_id` to equal the Video collection; see {@link requestVideoUploadUrl}.
 */
export interface VideoUploadUrlData {
  upload_id: string;
  s3_path: string;
  /** The Video collection the key sits under. Taken from the response, else read from s3_path. */
  collection_id: string;
  mime_type: string;
  expires_at?: string;
  post: VideoUploadPost;
}

/** PROVISIONAL — pending T3. Raw presign response; `collection_id`/`expires_at` optional. */
interface VideoUploadUrlResponseDto {
  upload_id: string;
  s3_path: string;
  collection_id?: string;
  expires_at?: string;
  post: VideoUploadPost;
}

export interface RegisterVideoAssetRequest {
  upload_id: string;
  s3_path: string;
  filename: string;
  mime_type: string;
  /** Must be the Video collection; register rejects anything else. */
  collection_id: string;
  tags?: string[];
}

/** Asset row as `registerAsset.ts` returns it (`ASSET_RETURNING`). */
export interface RegisteredVideoAssetRow {
  id: string;
  collection_id: string | null;
  filename: string;
  s3_path: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  tags: string[] | null;
  topic_id: string | null;
  industry: string | null;
  image_use: string;
  ai_generated: boolean;
  created_at: string;
  updated_at: string;
  media_kind: MediaKind;
  video_probe_state: VideoProbeState | null;
}

/** `POST /media/assets` (video) → 201 `{ asset, scan_state }` — `"scanning"`, or `"clean"` in dev with the scan auto-clean flag on. */
export interface RegisteredVideoAsset {
  asset: RegisteredVideoAssetRow;
  scan_state: VideoScanState;
}

/** `publicAsset()` in `getAsset.ts`, plus a version-pinned preview URL once clean and probe-ok. */
export interface MediaAssetDetail {
  id: string;
  collection_id: string | null;
  filename: string;
  s3_path: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  tags: string[] | null;
  media_kind: MediaKind;
  /** User id (not a display name). */
  uploaded_by: string;
  uploaded_at: string;
  updated_at: string;
  usage_count: number;
  /** Video only: the probe's measured resolution; null until the probe has run. */
  video_width?: number | null;
  video_height?: number | null;
  preview_url?: string;
  preview_url_expires_at?: string;
}

/**
 * `GET /media/assets/{id}`. For a video all three status fields are present; for an image the
 * response is `{ asset }` only.
 */
export interface MediaAssetStatus {
  asset: MediaAssetDetail;
  scan_state?: VideoScanState;
  probe_state?: VideoProbeState | null;
  probe_reason?: VideoProbeReason | null;
}

/** One row of `GET /media/assets?media_kind=video` (`publicVideo()` in `listAssets.ts`). */
export interface VideoAssetListItem extends MediaAssetListItem {
  media_kind: 'video';
  /** User id (not a display name). */
  uploaded_by?: string;
  video_probe_state: VideoProbeState | null;
  video_duration_seconds: number | null;
  video_width?: number | null;
  video_height?: number | null;
  video_codec?: string | null;
  /** Probe warnings, e.g. `['not_faststart']`. Shape not pinned by the API. */
  video_warnings?: unknown;
}

export type ListVideoAssetsParams = Omit<ListMediaAssetsParams, 'image_use' | 'media_kind'>;

/** Thrown by {@link getMediaAsset} on 404 — includes an infected video, which is soft-deleted. */
/** 409 asset_in_use from DELETE /media/assets/{id}: slides still use it. */
export class MediaAssetInUseError extends Error {
  constructor(
    public readonly assetId: string,
    public readonly usageCount: number,
  ) {
    super(`Media asset ${assetId} is used on ${usageCount} slide(s)`);
    this.name = 'MediaAssetInUseError';
  }
}

export class MediaAssetNotFoundError extends Error {
  constructor(public readonly assetId: string) {
    super(`Media asset ${assetId} not found`);
    this.name = 'MediaAssetNotFoundError';
  }
}

const VIDEO_MIME_BY_EXTENSION: Record<string, string> = {
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
};

function videoMimeType(file: File): string {
  if (file.type) {
    return file.type;
  }
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  return VIDEO_MIME_BY_EXTENSION[extension] ?? 'application/octet-stream';
}

/** `content-authoring/media-collections/{collection-id}/{upload-id}.{ext}` → collection id. */
function collectionIdFromS3Path(s3Path: string): string {
  if (!s3Path.startsWith(MEDIA_PREFIX)) {
    return '';
  }
  return s3Path.slice(MEDIA_PREFIX.length).split('/')[0] ?? '';
}

/**
 * POST /media/upload-url with `media_kind: "video"` — presigned S3 POST for a video.
 *
 * PROVISIONAL — pending T3 (ST-01). The server picks the Video collection; register then insists
 * on it. The TAR response does not carry `collection_id`, so it is read from `s3_path` (the
 * folder segment) when absent. That is reading the key the server built, not building one.
 */
export async function requestVideoUploadUrl({ file }: { file: File }): Promise<VideoUploadUrlData> {
  const mimeType = videoMimeType(file);
  const response = await http.post(
    toAuthoringApiUrl('/media/upload-url').toString(),
    JSON.stringify({
      filename: file.name,
      mime_type: mimeType,
      file_size_bytes: file.size,
      media_kind: 'video',
    }),
  );

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to get upload URL (${response.status})`));
  }

  const body = (await response.json()) as VideoUploadUrlResponseDto;
  return {
    upload_id: body.upload_id,
    s3_path: body.s3_path,
    collection_id: body.collection_id ?? collectionIdFromS3Path(body.s3_path),
    mime_type: mimeType,
    expires_at: body.expires_at,
    post: body.post,
  };
}

/**
 * POST the file to S3 with the presigned form from {@link requestVideoUploadUrl}. Rejects with an
 * `AbortError` when `signal` aborts.
 */
export async function uploadVideoToS3(
  post: VideoUploadPost,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  await uploadFileWithProgress(post.url, file, onProgress ?? (() => undefined), {
    method: 'POST',
    formFields: post.fields,
    signal,
  });
}

/**
 * POST /media/assets with `media_kind: "video"` — register an uploaded video. The server checks
 * size and type against what S3 holds, pins the version, and answers 201 with
 * `scan_state: "scanning"`; a bad upload is deleted and answered 422 `video_upload_invalid`.
 *
 * `signal` mirrors {@link uploadVideoToS3}: it lets a cancel during this stage actually abort the
 * in-flight request (rejecting with `AbortError`) instead of letting it complete behind the
 * caller's back after the UI has already told the author the upload was cancelled.
 */
export async function registerVideoAsset(
  payload: RegisterVideoAssetRequest,
  signal?: AbortSignal,
): Promise<RegisteredVideoAsset> {
  const response = await http.post(
    toAuthoringApiUrl('/media/assets').toString(),
    JSON.stringify({
      upload_id: payload.upload_id,
      s3_path: payload.s3_path,
      filename: payload.filename,
      mime_type: payload.mime_type,
      collection_id: payload.collection_id,
      tags: payload.tags ?? [],
      media_kind: 'video',
    }),
    { signal },
  );

  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to register video (${response.status})`));
  }

  return (await response.json()) as RegisteredVideoAsset;
}

/**
 * GET /media/assets/{id} — one asset with, for a video, its live scan and probe state. The scan
 * state is read from the S3 tag on every call. Throws {@link MediaAssetNotFoundError} on 404.
 */
export async function getMediaAsset(id: string): Promise<MediaAssetStatus> {
  const response = await http.get(
    toAuthoringApiUrl(`/media/assets/${encodeURIComponent(id)}`).toString(),
  );

  if (response.status === 404) {
    throw new MediaAssetNotFoundError(id);
  }
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, `Failed to load media asset (${response.status})`));
  }

  return (await response.json()) as MediaAssetStatus;
}

/**
 * GET /media/assets?media_kind=video — the Video folder. Every non-deleted video is returned; only
 * a clean, probe-ok one carries a (version-pinned) preview or download URL.
 */
export async function listVideoAssets(
  params: ListVideoAssetsParams = {},
): Promise<{ assets: VideoAssetListItem[]; pagination: MediaCollectionsPagination }> {
  const result = await listMediaAssets({ ...params, media_kind: 'video' });
  return {
    assets: result.assets as VideoAssetListItem[],
    pagination: result.pagination,
  };
}

export interface ResolvedAsset {
  url: string;
  /** Epoch ms the signature expires, so a caller can refresh before it does. */
  expiresAt: number;
}

/**
 * The query string one batch will send. Shared by the batcher and the request so the string that
 * was measured is byte-for-byte the string that goes out — measuring an approximation of it is how
 * a limit like this gets missed by a few bytes.
 *
 * `URLSearchParams` percent-encodes to ASCII, so `.length` is the byte count.
 */
function resolveQuery(chunk: readonly string[]): string {
  const params = new URLSearchParams();
  params.set('s3_paths', chunk.join(','));
  params.set('limit', String(chunk.length));
  return params.toString();
}

/**
 * Splits a deck's keys into batches that fit {@link QUERY_STRING_BUDGET}, and `MAX_RESOLVE_KEYS`.
 *
 * Greedy rather than a fixed keys-per-request number, because key length is not constant: keys
 * written before CEE-919 are topic-keyed and longer than today's collection-keyed ones, so a deck
 * can mix both. Counting keys would be right for one shape and wrong for the other.
 *
 * A batch always takes at least one key, so an oversized single key produces a request rather than
 * an empty batch and an infinite loop. It cannot 403: the Lambda caps a path at 1,000 characters
 * (`MAX_S3_PATH_LENGTH`), which stays inside the budget even fully percent-encoded.
 */
function chunkKeysForQueryLimit(keys: readonly string[]): string[][] {
  const chunks: string[][] = [];
  let current: string[] = [];

  for (const key of keys) {
    const full =
      current.length >= MAX_RESOLVE_KEYS ||
      resolveQuery([...current, key]).length > QUERY_STRING_BUDGET;
    if (current.length > 0 && full) {
      chunks.push(current);
      current = [];
    }
    current.push(key);
  }

  if (current.length > 0) chunks.push(current);
  return chunks;
}

/**
 * Resolves S3 keys to presigned preview URLs.
 *
 * Keys outside the media-collections prefix are dropped before the request, and keys with no
 * matching row are simply absent from the result — a template can reference an asset that was
 * since deleted, and one stale key must not blank every other image on the deck.
 *
 * @param imageKeys - S3 keys from a deck's slides; duplicates and blanks are tolerated
 * @throws Error when the request itself fails, so the caller can retry or surface it
 */
export async function resolveImageUrls(
  imageKeys: readonly string[],
): Promise<Map<string, ResolvedAsset>> {
  const keys = Array.from(new Set(imageKeys.filter(Boolean))).filter(isResolvableImageKey);
  const resolved = new Map<string, ResolvedAsset>();
  if (keys.length === 0) return resolved;

  // Chunks go out together rather than one after another (CEE-773). A 46-image deck is three
  // batches, so serially it cost three round trips before the first image could paint.
  //
  // This is safe to parallelise precisely because of two properties above: `keys` is deduped
  // through a Set BEFORE chunking, so no key appears in two chunks and the merge order cannot
  // change the result; and a failed chunk still throws, so `Promise.all` rejecting preserves
  // this function's all-or-nothing contract — the caller never sees a partial map. The only
  // difference on the failure path is that the remaining requests are already in flight
  // instead of never sent.
  const bodies = await Promise.all(
    chunkKeysForQueryLimit(keys).map(async (chunk): Promise<AssetsResponseDto> => {
      const url = toAuthoringApiUrl('/media/assets');
      url.search = resolveQuery(chunk);

      const response = await http.get(url.toString());
      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, `Failed to resolve slide images (${response.status})`));
      }

      return (await response.json()) as AssetsResponseDto;
    }),
  );

  for (const body of bodies) {
    for (const asset of body.assets ?? []) {
      if (!asset.s3_path || !asset.preview_url) continue;
      const expiry = asset.preview_url_expires_at
        ? Date.parse(asset.preview_url_expires_at)
        : Number.NaN;
      resolved.set(asset.s3_path, {
        url: asset.preview_url,
        // An unparseable or missing expiry is treated as already stale rather than eternal:
        // the URL still renders now, and the next refresh check re-fetches it.
        expiresAt: Number.isNaN(expiry) ? 0 : expiry,
      });
    }
  }

  return resolved;
}

/**
 * Get presigned preview URL for a video asset (CEE-1354 T4).
 * URL lifetime is 15 minutes. Caller should refresh every 10 minutes.
 *
 * Always sends `media_kind=video` — the list API defaults to image, which would miss every
 * video row (and the CDK-deployed placeholder, which is signed only on the video path).
 */
export async function getVideoPreviewUrl(videoKey: string): Promise<{ url: string } | null> {
  if (!videoKey) return null;

  const url = toAuthoringApiUrl('/media/assets');
  const params = new URLSearchParams(resolveQuery([videoKey]));
  params.set('media_kind', 'video');
  url.search = params.toString();

  const response = await http.get(url.toString());
  if (!response.ok) {
    throw new Error(
      await readApiErrorMessage(response, `Failed to resolve video preview URL (${response.status})`)
    );
  }

  const body = (await response.json()) as AssetsResponseDto;
  const asset = body.assets?.[0];

  if (!asset?.preview_url) return null;

  return { url: asset.preview_url };
}
